import { useRef } from "react";
import {
    CheckCircle2,
    Download,
    DotIcon,
    File,
    FileArchive,
    FileImage,
    FilePlay,
    FileSpreadsheet,
    FileText,
    FileType,
    FileVideo,
    Loader2,
    RotateCcw,
    Trash2,
    Upload,
    X
} from "lucide-react";
import Moment from "react-moment";
import Skeleton from "react-loading-skeleton";
import { BotaoBranco } from "../Common/styles";
import { comum } from "../../../styles/Themes/light";
import { IAnexo } from "../../../utility/IAnexo";
import { formatByteSize, truncarNomeArquivo } from "../../../utility/byteSizeUtils";
import {
    AreaUpload,
    BarraProgresso,
    CabecalhoLista,
    CartaoAnexo,
    CartaoUpload,
    Container,
    ListaAnexos,
    ListaVazia
} from "./styles";
import { ArquivoUploadInfo, extrairExtensao, extrairNomeArquivo, obterInformacoesResponsavel } from "./utils";

const escolherIcone = (extensao: string) => {
    const ext = extensao.toLowerCase();

    if (ext === "pdf") {
        return <FileText color={comum.colors.primaryRed400} />;
    }

    if (["doc", "docx", "rtf", "odt", "txt", "pages"].includes(ext)) {
        return <FileType color={comum.colors.primaryBlue400} />;
    }

    if (["xls", "xlsx", "xlsm", "xltx", "ods", "csv", "tsv"].includes(ext)) {
        return <FileSpreadsheet color={comum.colors.secondaryGreen400} />;
    }

    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "tiff", "tif", "svg", "heic", "heif"].includes(ext)) {
        return <FileImage color={comum.colors.secondaryPool400} />;
    }

    if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "tgz", "tbz"].includes(ext)) {
        return <FileArchive color={comum.colors.primaryOrange500} />;
    }

    if (["mp3", "wav", "aac", "m4a", "ogg", "flac", "wma"].includes(ext)) {
        return <FilePlay color={comum.colors.primaryBlue500} />;
    }

    if (["mp4", "avi", "mov", "mkv", "wmv", "webm", "m4v"].includes(ext)) {
        return <FileVideo color={comum.colors.primaryOrange400} />;
    }

    return <File color={comum.colors.interfaceGray400} />;
};

interface ComponenteArquivosProps {
    titulo?: string;
    anexos: IAnexo[];
    uploads: ArquivoUploadInfo[];
    carregando?: boolean;
    baixando?: number | null;
    excluindo?: number | null;
    somenteLeitura?: boolean;
    arrastando?: boolean;
    tamanhoTotal?: string;
    podeExcluir?: boolean;
    aoSelecionarArquivos: (files: FileList | null) => void;
    aoTentarNovamente: (indice: number) => void;
    aoCancelar: (indice: number) => void;
    aoBaixar: (anexo: IAnexo, indice: number) => void;
    aoExcluir?: (anexo: IAnexo, indice: number) => void;
    aoArrastarSobre?: (evento: React.DragEvent) => void;
    aoSairArrasto?: (evento: React.DragEvent) => void;
    aoSoltarArquivos?: (evento: React.DragEvent) => void;
}

const TITULO_PADRAO = "Arquivos Anexados";

export default function ComponenteArquivos({
    titulo = TITULO_PADRAO,
    anexos,
    uploads,
    carregando = false,
    baixando = null,
    excluindo = null,
    somenteLeitura = false,
    arrastando = false,
    tamanhoTotal = "0 B",
    podeExcluir = false,
    aoSelecionarArquivos,
    aoTentarNovamente,
    aoCancelar,
    aoBaixar,
    aoExcluir,
    aoArrastarSobre,
    aoSairArrasto,
    aoSoltarArquivos
}: ComponenteArquivosProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const abrirSeletorArquivos = () => {
        if (somenteLeitura) {
            return;
        }
        inputRef.current?.click();
    };

    const aoAlterarInput = (evento: React.ChangeEvent<HTMLInputElement>) => {
        aoSelecionarArquivos(evento.target.files);
        evento.target.value = "";
    };

    const quantidadeAnexos = anexos.length;
    const possuiConteudo = quantidadeAnexos > 0 || uploads.length > 0 || carregando;

    return (
        <Container>
            {!somenteLeitura && (
                <AreaUpload
                    $arrastando={arrastando}
                    onDragEnter={aoArrastarSobre}
                    onDragOver={(evento) => {
                        evento.preventDefault();
                        evento.stopPropagation();
                    }}
                    onDragLeave={aoSairArrasto}
                    onDrop={aoSoltarArquivos}
                >
                    <div className="icone-upload">
                        <Upload />
                    </div>
                    <div className="textos-upload">
                        <p className="principal">{arrastando ? "Solte os arquivos aqui" : "Arraste arquivos para cá"}</p>
                        <p className="secundario">ou clique para selecionar arquivos</p>
                    </div>
                    <div className="acoes-upload">
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            onChange={aoAlterarInput}
                            style={{ display: "none" }}
                        />
                        <BotaoBranco type="button" onClick={abrirSeletorArquivos}>
                            <Upload size={16} />
                            Selecionar Arquivos
                        </BotaoBranco>
                    </div>
                </AreaUpload>
            )}

            {possuiConteudo ? (
                <>
                    <CabecalhoLista>
                        <h2>
                            {titulo} ({quantidadeAnexos})
                        </h2>
                        <p>{tamanhoTotal} total</p>
                    </CabecalhoLista>
                    <ListaAnexos>
                        {uploads.map((upload, indice) => {
                            const nomeUpload = upload.arquivo.name;
                            const nomeTruncado = truncarNomeArquivo(nomeUpload);
                            const extensao = extrairExtensao(nomeUpload);
                            const iconeUpload = escolherIcone(extensao);

                            return (
                                <CartaoUpload key={`upload-${indice}`} $status={upload.status}>
                                    <div className="icone">{iconeUpload}</div>
                                    <div className="detalhes">
                                        <div className="cabecalho">
                                            <div className="nome" title={nomeUpload}>{nomeTruncado}</div>
                                            <div className="extensao">{extensao}</div>
                                        </div>
                                        {upload.status === "enviando" && (
                                            <BarraProgresso $progresso={upload.progresso}>
                                                <div className="trilha">
                                                    <div className="preenchimento" />
                                                </div>
                                                <div className="percentual">{upload.progresso}%</div>
                                            </BarraProgresso>
                                        )}
                                        <div className="rodape">
                                            <span>{formatByteSize(upload.arquivo.size)}</span>
                                            <DotIcon size={16} />
                                            {upload.status === "enviando" && <span>Enviando...</span>}
                                            {upload.status === "sucesso" && (
                                                <span className="mensagem-sucesso">Enviado com sucesso</span>
                                            )}
                                            {upload.status === "erro" && (
                                                <span className="mensagem-erro">{upload.erro || "Erro ao enviar"}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="acoes">
                                        {upload.status === "enviando" && (
                                            <Loader2 size={16} className="carregando" color={comum.colors.primaryRed400} />
                                        )}
                                        {upload.status === "sucesso" && (
                                            <CheckCircle2 size={16} color={comum.colors.secondaryGreen400} />
                                        )}
                                        {upload.status === "erro" && (
                                            <>
                                                <button type="button" onClick={() => aoTentarNovamente(indice)}>
                                                    <RotateCcw size={16} color={comum.colors.primaryRed400} />
                                                </button>
                                                <button type="button" onClick={() => aoCancelar(indice)}>
                                                    <X size={16} color={comum.colors.interfaceGray400} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </CartaoUpload>
                            );
                        })}

                        {carregando && (
                            <>
                                {[1, 2, 3].map((chave) => (
                                    <CartaoAnexo key={`skeleton-${chave}`}>
                                        <div className="icone">
                                            <Skeleton circle width={25} height={25} />
                                        </div>
                                        <div className="conteudo">
                                            <div className="cabecalho">
                                                <Skeleton width={180} height={13} />
                                                <Skeleton width={50} height={18} borderRadius={12} />
                                            </div>
                                            <div className="detalhes">
                                                <Skeleton width={120} height={11} />
                                            </div>
                                        </div>
                                        <div className="acao-download">
                                            <Skeleton circle width={25} height={25} />
                                        </div>
                                    </CartaoAnexo>
                                ))}
                            </>
                        )}

                        {!carregando &&
                            anexos.map((anexo, indice) => {
                                const nome = extrairNomeArquivo(anexo);
                                const extensao = extrairExtensao(nome);
                                const icone = escolherIcone(extensao);
                                const { responsavel, tipo } = obterInformacoesResponsavel(anexo);
                                const tamanho = formatByteSize(anexo.tamanho || 0);

                                return (
                                    <CartaoAnexo key={anexo.id_proposta_arquivo ?? `${nome}-${indice}`}>
                                        <div className="icone">{icone}</div>
                                        <div className="conteudo">
                                            <div className="cabecalho">
                                                <div className="nome">{nome}</div>
                                                <div className="extensao">{extensao}</div>
                                            </div>
                                            <div className="detalhes">
                                                <div>{tamanho}</div>
                                                <DotIcon size={16} />
                                                <div className="responsavel">
                                                    <span>Enviado por {responsavel}</span>
                                                    {tipo && <span className="etiqueta">{tipo}</span>}
                                                </div>
                                                <DotIcon size={16} />
                                                <div>
                                                    {anexo.criado ? (
                                                        <Moment locale="pt-br" format="DD/MM/YYYY HH:mm">
                                                            {anexo.criado}
                                                        </Moment>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <div
                                                className="acao-download"
                                                onClick={() => aoBaixar(anexo, indice)}
                                                role="button"
                                            >
                                                {baixando === indice ? (
                                                    <Loader2 size={18} className="carregando" color={comum.colors.interfaceGray400} />
                                                ) : (
                                                    <Download size={18} color={comum.colors.interfaceGray400} />
                                                )}
                                            </div>
                                            {podeExcluir && aoExcluir && (
                                                <div
                                                    className="acao-download"
                                                    onClick={() => aoExcluir(anexo, indice)}
                                                    role="button"
                                                    title="Excluir arquivo"
                                                    style={{ cursor: excluindo === indice ? 'not-allowed' : 'pointer', opacity: excluindo === indice ? 0.6 : 1 }}
                                                >
                                                    {excluindo === indice ? (
                                                        <Loader2 size={18} className="carregando" color={comum.colors.primaryRed400} />
                                                    ) : (
                                                        <Trash2 size={18} color={comum.colors.primaryRed400} />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </CartaoAnexo>
                                );
                            })}
                    </ListaAnexos>
                </>
            ) : (
                <ListaVazia>Nenhum arquivo anexado até o momento.</ListaVazia>
            )}
        </Container>
    );
}
